<template>
  <VDropdown @show="isShown = true" @hide="isShown = false">
    <button
      class="flex items-center ml-2 font-sans text-lg"
      :class="{ 'pointer-events-none': !hasMultipleTenants }"
    >
      <SvgIcon
        name="chart-network-light"
        class="flex-shrink-0 w-6 h-6 fill-current"
        aria-hidden="true"
      />
      <div class="ml-3">
        {{ activeTenant?.name || $t("navigation.tenantSwitch") }}
      </div>
    </button>

    <template v-if="hasMultipleTenants" #popper>
      <div class="py-2 px-1 w-[220px]">
        <button
          v-for="tenant in availableTenants"
          :key="tenant.tag"
          class="flex justify-between py-2.5 first:pt-0 w-full hover:text-blue-primary"
          :class="{ 'text-blue-primary': tenant.tag == activeTenant?.tag }"
          @click="setActiveTenant(tenant)"
        >
          <span>{{ tenant.name }}</span>
          <SvgIcon
            v-if="tenant.tag == activeTenant?.tag"
            name="check-regular"
            class="svg-icon--md text-blue-primary"
          />
        </button>
      </div>
    </template>
  </VDropdown>
</template>
<script lang="ts" setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import restoreSession from "@/helpers/sessionRestore";
import { useAuthStore } from "@/store/Auth";
import { useBaseStore } from "@/store/Base";
import { notifyUser } from "@/helpers/notificationHelper";

const isShown = ref(false);

const authStore = useAuthStore();
const baseStore = useBaseStore();

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const availableTenants = computed(() => {
  return authStore.availableTenants;
});

const activeTenant = computed({
  get() {
    if (!availableTenants.value) return;

    return availableTenants.value.find(
      (tenant) => tenant.tag == authStore.activeTenant
    );
  },
  async set(value: any) {
    if (!value) return;

    baseStore.setIsSwitchingTenant(true);

    // If we're on a dossier, push to home. It makes no sense to stay
    // on the dossier after a tenant switch
    if (route.name == "dossier") {
      // we can safely push to home because this TenantSwitch component
      // persists even if the route is changed
      // so the subsequent code is still being executed
      router.push({ name: "home" });
    }

    authStore.setActiveTenant(value.tag);

    if (!authStore.availableTenants) return;

    const tenantName = authStore.availableTenants.filter(
      (tenant) => tenant.tag == value.tag
    )[0].name;

    await restoreSession();
    baseStore.setIsSwitchingTenant(false);

    notifyUser({
      type: "sucess",
      text: t("general.tenantSwitched", {
        tenant: tenantName,
      }).toString(),
    });
  },
});

const setActiveTenant = (tenant: any) => {
  activeTenant.value = tenant;
};

const hasMultipleTenants = computed(() => {
  if (!authStore.availableTenants) return false;
  return authStore.availableTenants.length > 1;
});
</script>
